import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, Layout, Modal, Paragraph } from '@vwfs-bronson/bronson-react';
import { AxiosError } from 'axios';
import { Consents, getConsentsRequestCustomerData, submitConsents } from '../../../components/Consents/Consents';
import { LocalSpinner } from '../../../components/LocalSpinner';
import { displayApiError, SUPPRESS_ERROR_INTERCEPTOR } from '../../../services/api';
import {
  getCreditApplication,
  getCreditApplicationCustomerStipulations,
  submitCreditApplication,
} from '../../../services/api/miles/miles-credit-services';
import { getQuoteStakeholderContacts, linkStakeholderContact } from '../../../services/api/miles/miles-services';
import { updateDataStorageNew } from '../../../services/api/updateDataStorage/updateDataStorage';
import { getTransactionId } from '../../../services/common/data';
import { assertDefined, waitForMiles } from '../../../services/common/miscUtils';
import { staticSelector } from '../../../services/redux';
import {
  creditApplicationSelectors,
  setCanRedirectToOnfido,
  setCreditApplicationDetails,
} from '../../../services/redux/features/creditApplication.slice';
import { clearApiError, customSelectors } from '../../../services/redux/features/custom.slice';
import { customerSelectors } from '../../../services/redux/features/customer.slice';
import { DataStorageRequest } from '../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { getCustomerIdentData, getFormNamesToRender } from './utils';

const GATEWAY_TIMEOUT_STATUS = 504;
const MAX_SUBMISSION_POLL_ATTEMPTS = 30;
const IN_PROGRESS_STATUSES: CreditIntegration.Schemas.SolvencyCheck['status'][] = [
  'TO_BE_REQUESTED',
  'REQUESTED',
  'ANSWERED',
  'CDP_RETURN',
];

const isGatewayTimeout = (error: unknown): boolean => {
  const axiosError = error as AxiosError;

  // An API Gateway 504 is returned without CORS headers, so the browser blocks the response and axios
  return (
    axiosError?.response?.status === GATEWAY_TIMEOUT_STATUS || (Boolean(axiosError?.request) && !axiosError?.response)
  );
};

const pollForCreditDecision = async (
  creditApplicationId: string
): Promise<CreditIntegration.Schemas.CreditApplicationResponse> => {
  for (let attempt = 0; attempt < MAX_SUBMISSION_POLL_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await waitForMiles();

    try {
      // eslint-disable-next-line no-await-in-loop
      const creditApplication = await getCreditApplication(creditApplicationId, SUPPRESS_ERROR_INTERCEPTOR);
      const status = creditApplication?.creditDecision?.solvencyCheck?.status;

      if (status && !IN_PROGRESS_STATUSES.includes(status)) {
        return creditApplication;
      }
    } catch {
      // Backend still processing — retry until attempts are exhausted.
    }
  }

  throw new Error('Credit application decision unavailable after submission gateway timeout.');
};

const linkStakeholders = async (quoteId: string, directorId: string, signatoryId: string) => {
  const quoteStakeholderContactsResponse = await getQuoteStakeholderContacts(quoteId);
  const existingStakeholderQuoteLinks = quoteStakeholderContactsResponse.stakeholderContacts;

  const isStakeholderAlreadyLinked = (stakeholderId: string) => {
    return existingStakeholderQuoteLinks?.find((sc) => sc.milesStakeholderContactId === stakeholderId);
  };

  if (!isStakeholderAlreadyLinked(directorId)) {
    await linkStakeholderContact(quoteId, { milesStakeholderContactId: directorId });
  }

  if (!isStakeholderAlreadyLinked(signatoryId)) {
    await linkStakeholderContact(quoteId, { milesStakeholderContactId: signatoryId });
  }
};

type Props = {
  isCoCustomer?: boolean;
  hasCoCustomer?: boolean;
  isSoleTrader?: boolean;
  customerType: CreditIntegration.Schemas.CustomerType;
};

export const CustomerDetailsSubmitSection: FunctionComponent<Props> = ({
  isCoCustomer,
  hasCoCustomer,
  customerType,
  isSoleTrader,
}) => {
  const [creditModalVisible, setCreditModalVisible] = useState(false);
  const isHybridJourney = useSelector(customSelectors.selectIsHybridJourney);
  const stakeholderContacts = useSelector(customerSelectors.selectStakeholderContacts);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isSignatoryFormActive = useSelector(customerSelectors.selectIsSignatoryFormActive);

  const goBackHandler = () => {
    if (isCoCustomer) navigate.form('/4');
    else navigate.form('/3');
  };

  const waitWithSubmitForCoCustomer = hasCoCustomer && !isCoCustomer;

  const submittedForms = useSelector(creditApplicationSelectors.selectSubmittedForms);
  const formNamesToRender = getFormNamesToRender({ customerType, isSoleTrader, isCoCustomer });
  const allFormsSubmitted = formNamesToRender.every((formName) => submittedForms.includes(formName));

  const goForwardHandler = async () => {
    const { quoteId, creditApplicationId } = staticSelector(creditApplicationSelectors.selectCreditApplicationIds);

    dispatch(loadingActions.setLoading(true));

    const applicationFormHasStakeholders = customerType === 'LEGAL_ENTITY' && !isSoleTrader;

    if (applicationFormHasStakeholders) {
      const directorContactId = assertDefined(
        stakeholderContacts.DIRECTOR?.milesStakeholderContactId,
        'Director milesStakeholderContactId was not defined.'
      );

      const signatoryContactId = assertDefined(
        isSignatoryFormActive
          ? stakeholderContacts.SIGNATORY?.milesStakeholderContactId
          : stakeholderContacts.DIRECTOR?.signatoryStakeholderContactId,
        'Signatory milesStakeholderContactId was not defined.'
      );

      linkStakeholders(quoteId, directorContactId, signatoryContactId);
    }

    await submitConsents(getConsentsRequestCustomerData(customerType, isCoCustomer, isSoleTrader))
      .finally(() => {
        dispatch(loadingActions.setLoading(false));
      })
      .catch(navigate.error);

    if (waitWithSubmitForCoCustomer) {
      navigate.form(4, '/coContractor');
      return;
    }

    setCreditModalVisible(true);

    let creditApplication = {} as CreditIntegration.Schemas.CreditApplicationResponse;

    try {
      const creditApplicationCustomerData = staticSelector(
        creditApplicationSelectors.selectCreditApplicantCustomerData
      );
      const creditApplicationCoCustomerData = staticSelector(
        creditApplicationSelectors.selectCreditApplicantCustomerDataCoCustomer
      );

      let dataStorageRequest: DataStorageRequest;

      if (creditApplicationCustomerData.customerType === 'INDIVIDUAL' && !isSoleTrader) {
        dataStorageRequest = {
          custom: {
            ident: {
              mainCustomer: getCustomerIdentData(creditApplicationCustomerData, 'INDIVIDUAL'),
              coCustomer:
                creditApplicationCoCustomerData && Object.keys(creditApplicationCoCustomerData).length > 0
                  ? getCustomerIdentData(creditApplicationCoCustomerData, 'COCONTRACTOR')
                  : undefined,
            },
          },
        };
      } else if (creditApplicationCustomerData.customerType === 'LEGAL_ENTITY' && isSoleTrader) {
        dataStorageRequest = {
          custom: {
            ident: {
              mainCustomer: getCustomerIdentData(creditApplicationCustomerData, 'LEGAL_ENTITY_SOLE_TRADER'),
              coCustomer: undefined,
            },
          },
        };
      } else if (creditApplicationCustomerData.customerType === 'LEGAL_ENTITY') {
        dataStorageRequest = {
          custom: {
            ident: {
              mainCustomer: getCustomerIdentData(creditApplicationCustomerData, 'DIRECTOR'),
              coCustomer: isSignatoryFormActive
                ? getCustomerIdentData(creditApplicationCustomerData, 'SIGNATORY')
                : undefined,
            },
          },
        };
      } else {
        throw new Error('Error during mapping ident data: Unrecognized customer type.');
      }

      await updateDataStorageNew(getTransactionId(), dataStorageRequest);

      let submissionTimedOut = false;

      try {
        await submitCreditApplication(quoteId, creditApplicationId!, SUPPRESS_ERROR_INTERCEPTOR);
      } catch (submitError) {
        if (!isGatewayTimeout(submitError)) {
          // The suppressed interceptor skipped the modal; show it for a genuine failure.
          displayApiError(submitError);
          throw submitError;
        }
        // 504: the backend is still processing or already finished — recover by polling below.
        submissionTimedOut = true;
        dispatch(clearApiError());
      }

      if (submissionTimedOut) {
        try {
          creditApplication = await pollForCreditDecision(creditApplicationId!);
        } catch (pollError) {
          displayApiError(pollError);
          throw pollError;
        }
      } else {
        await waitForMiles();
        creditApplication = await getCreditApplication(creditApplicationId!);
      }

      dispatch(setCreditApplicationDetails(creditApplication));
    } catch (e) {
      navigate.error(e);
      return;
    } finally {
      setCreditModalVisible(false);
    }

    const status = creditApplication.creditDecision?.solvencyCheck?.status;

    const checkKoTechniqueRedirection = async () => {
      try {
        const receivedStipulations = await getCreditApplicationCustomerStipulations(quoteId, creditApplicationId!);
        const hasProperStatus = receivedStipulations?.documents?.some((stipulation) =>
          ['CREATED', 'RECEIVED', 'ACTIVATED'].includes(stipulation.stipulationStatus!)
        );

        if (!hasProperStatus) {
          dispatch(setCanRedirectToOnfido(false));
        }
      } catch (error) {
        dispatch(setCanRedirectToOnfido(false));
      } finally {
        navigate.creditCheckPending();
      }
    };

    switch (status) {
      case 'REFUSED':
      case 'REFUSED_AUTO':
      case 'CANCELED':
      case 'REFUSED_AFST':
      case 'REFUSE_FRAUD':
      case 'REFUSED_AFSP':
        dispatch(setCanRedirectToOnfido(false));
        navigate.creditCheckRejection();
        break;
      case 'CREATED':
      case 'ACCEPTATION_CONDITIONAL':
      case 'ACCEPTED_DEFINITIVE_AUTO':
      case 'ACCEPTED_WITH_RESERVATION_AUTO':
      case 'COMPLETED':
        dispatch(setCanRedirectToOnfido(true));
        navigate.creditCheckSuccess();
        break;
      case 'STUDY':
      case 'STUDY_AUTO':
        dispatch(setCanRedirectToOnfido(true));
        navigate.creditCheckPending();
        break;
      case 'KO_TECHNIQUE':
        checkKoTechniqueRedirection();
        break;
      case 'TO_BE_REQUESTED':
      case 'REQUESTED':
      case 'ANSWERED':
      case 'CDP_RETURN':
      case 'CANCELED_AUTO':
      default:
        dispatch(setCanRedirectToOnfido(false));
        navigate.creditCheckPending();
        break;
    }
  };

  const goBackText = t('step4:navigation:return');

  const goForwardText = hasCoCustomer && !isCoCustomer ? t('step4:navigation:next') : t('step4:navigation:submit');

  const isGoBackDisabled = isCoCustomer ? false : isHybridJourney;

  return (
    <>
      <Modal
        id="modal"
        shown={creditModalVisible}
        title={t('step4:modal:creditCheckModalTitle')}
        hideCloseButton
        hideTitle
      >
        <Layout center>
          <Layout.Item default="1/1">{creditModalVisible && <LocalSpinner center />}</Layout.Item>
          <Layout.Item default="1/1">
            <Paragraph>{t('step4:creditCheck')}</Paragraph>
          </Layout.Item>
        </Layout>
      </Modal>

      <Layout>
        <Layout.Item>
          <Consents />
        </Layout.Item>

        <Layout.Item>
          <ButtonContainer center>
            <Button type="button" secondary disabled={isGoBackDisabled} full onClick={goBackHandler}>
              {goBackText}
            </Button>

            <Button full type="button" disabled={!allFormsSubmitted} onClick={goForwardHandler}>
              {goForwardText}
            </Button>
          </ButtonContainer>
        </Layout.Item>
      </Layout>
    </>
  );
};
